/**
 * useAnalytics Hook - Analytics feature hooks
 *
 * React Query hooks for fetching analytics data
 */

import { useQuery } from "@tanstack/react-query";
import {
  getCustomerAdoptionStatusApi,
  getQuoteConversionByRegionApi,
  getConversionAnalysisApi,
  getCustomerRankingByRevenueApi,
  getProductPerformanceApi,
} from "../api";
import { queryKey } from "../constants";
import { DateRangeFilterType } from "../types";

/**
 * Hook to get customer adoption status data
 * @param dateRange - The date range filter to apply (default: 'lastmonth')
 * @param region - Optional region filter
 * @returns React Query result with customer adoption status data
 */
export const useCustomerAdoptionStatus = (
  dateRange: DateRangeFilterType = "lastmonth",
  region?: number
) => {
  return useQuery({
    queryKey: [queryKey.getCustomerAdoptionStatus, dateRange, region],
    queryFn: () => getCustomerAdoptionStatusApi(dateRange, region),
  });
};

/**
 * Hook to get quote conversion by region data
 * @param dateRange - The date range filter to apply (default: 'all')
 * @param region - Optional region filter
 * @returns React Query result with quote conversion by region data
 */
export const useQuoteConversionByRegion = (dateRange: string = "all", region?: number) => {
  return useQuery({
    queryKey: [queryKey.getQuoteConversionByRegion, dateRange, region],
    queryFn: () => getQuoteConversionByRegionApi(dateRange, region),
  });
};

/**
 * Hook to get conversion analysis data
 * @param dateRange - The date range filter to apply (default: 'all')
 * @param region - Optional region filter
 * @returns React Query result with conversion analysis data
 */
export const useConversionAnalysis = (dateRange: string = "all", region?: number) => {
  return useQuery({
    queryKey: [queryKey.getConversionAnalysis, dateRange, region],
    queryFn: () => getConversionAnalysisApi(dateRange, region),
  });
};

/**
 * Hook to get customer ranking by revenue data
 * @param dateRange - The date range filter to apply (default: 'all')
 * @param region - Optional region filter
 * @returns React Query result with customer ranking by revenue data
 */
export const useCustomerRankingByRevenue = (dateRange: string = "all", region?: number) => {
  return useQuery({
    queryKey: [queryKey.getCustomerRankingByRevenue, dateRange, region],
    queryFn: () => getCustomerRankingByRevenueApi(dateRange, region),
  });
};

/**
 * Hook to get product performance data
 * @param region - Optional region filter
 * @returns React Query result with product performance data
 */
export const useProductPerformance = (region?: number) => {
  return useQuery({
    queryKey: [queryKey.getProductPerformance, region],
    queryFn: () => getProductPerformanceApi(region),
  });
};
